import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getToken, API_BASE } from "@/lib/api";
import { notifyThreadsChanged } from "@/hooks/useUnreadThreads";

/**
 * Global user-level WebSocket for real-time push notifications.
 * Triggers toasts for new_job events. Auto-reconnects.
 */
export function useNotificationSocket(enabled) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    const token = getToken();
    if (!token) return;

    const connect = () => {
      const base = API_BASE.replace(/^http/, "ws");
      const url = `${base}/ws/notifications?token=${encodeURIComponent(token)}`;
      let ws;
      try {
        ws = new WebSocket(url);
      } catch {
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        timerRef.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => {};
      ws.onmessage = (ev) => {
        try {
          const evt = JSON.parse(ev.data);
          if (evt.type === "new_job" && evt.request) {
            const r = evt.request;
            const miles = r.distance_miles ? `${r.distance_miles.toFixed(0)} mi · ` : "";
            toast.success(
              `New haul: ${r.title}`,
              {
                description: `${r.pickup_city} → ${r.delivery_city} · ${miles}$${r.vehicle_type}`,
                action: {
                  label: "View",
                  onClick: () => {
                    window.location.href = `/app/requests/${r.id}`;
                  },
                },
              }
            );
          } else if (evt.type === "expiring_soon" && evt.request) {
            const r = evt.request;
            const n = r.bid_count ?? 0;
            toast.warning(
              `24h left: ${r.title}`,
              {
                description: `${n === 0 ? "No bids yet" : `${n} bid${n === 1 ? "" : "s"} so far`} — consider relisting or adjusting your budget.`,
                action: {
                  label: "Review",
                  onClick: () => {
                    window.location.href = `/app/requests/${r.id}`;
                  },
                },
              }
            );
          } else if (evt.type === "saved_search_match" && evt.request) {
            const r = evt.request;
            toast.success(
              `Instant match: ${r.title}`,
              {
                description: `Matches "${evt.search_name}" — ${r.pickup_city} → ${r.delivery_city}${r.budget_usd ? ` · $${Number(r.budget_usd).toLocaleString()}` : ""}. First bids win.`,
                action: {
                  label: "Bid now",
                  onClick: () => {
                    window.location.href = `/app/requests/${r.id}`;
                  },
                },
              }
            );
          } else if (evt.type === "saved_search_digest" && evt.count) {
            toast.success(
              `${evt.count} new haul${evt.count === 1 ? "" : "s"} match your saved searches`,
              {
                description: `via ${(evt.searches || []).join(", ")}`,
                action: evt.top?.id
                  ? {
                      label: "View top match",
                      onClick: () => {
                        window.location.href = `/app/requests/${evt.top.id}`;
                      },
                    }
                  : undefined,
              }
            );
          } else if (evt.type === "review_reminder" && evt.request) {
            toast.info(
              "How was the haul?",
              {
                description: `Rate your ${evt.counterpart || "counterpart"} for "${evt.request.title}" — 30 seconds keeps the marketplace clean.`,
                action: {
                  label: "Rate now",
                  onClick: () => {
                    window.location.href = `/app/requests/${evt.request.id}`;
                  },
                },
              }
            );
          } else if (evt.type === "counter_offer" && evt.request) {
            toast.warning(
              `Counter-offer: $${(evt.price_usd || 0).toLocaleString()}`,
              {
                description: `${evt.request.title}${evt.note ? ` — "${evt.note}"` : ""}. Accept or decline in the bid thread.`,
                action: {
                  label: "Respond",
                  onClick: () => {
                    window.location.href = `/app/requests/${evt.request.id}`;
                  },
                },
              }
            );
          } else if (evt.type === "bid_message" && evt.request) {
            toast.info(`Message from ${evt.sender_name || "bidder"}`, {
              description: `${evt.request.title} — ${evt.preview || ""}`,
              action: {
                label: "Reply",
                onClick: () => {
                  window.location.href = `/app/requests/${evt.request.id}`;
                },
              },
            });
          } else if ((evt.type === "counter_accepted" || evt.type === "counter_declined" || evt.type === "bid_accepted") && evt.request) {
            const labels = {
              counter_accepted: "Counter accepted — job assigned",
              counter_declined: "Counter declined",
              bid_accepted: "Your bid won",
            };
            toast[evt.type === "counter_declined" ? "warning" : "success"](labels[evt.type], {
              description: `${evt.request.title}${evt.price_usd ? ` · $${evt.price_usd.toLocaleString()}` : ""}`,
              action: {
                label: "View",
                onClick: () => {
                  window.location.href = `/app/requests/${evt.request.id}`;
                },
              },
            });
          }
          if (["bid_message", "counter_offer", "counter_accepted", "counter_declined", "bid_accepted"].includes(evt.type)) {
            notifyThreadsChanged();
          }
        } catch {}
      };
    };
    connect();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        ws.onclose = null;
        try { ws.close(); } catch {}
      }
    };
  }, [enabled]);

  return { connected };
}
